import { useState } from 'react'
import { Button, Popover, Select, Stack, Tooltip } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { showNotification } from '@mantine/notifications'
import { IconCalendar } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BILLING_STATUS_LABELS,
  BILLING_STATUS_STYLES,
  isBillingStatusValue,
  type BillingStatusValue,
} from '@/pages/Billing/utils'
import { managedCaseRefusal } from '@/model/middleware-types'
import { useOrpc } from '@/hooks/useClient'
import { manualStatusOptions, normalizeBillingUpdateDate, todayBillingDate } from '../utils'
import { PillLabel, StatusPill } from './StatusPill'

type ClaimStatusControlProps = {
  encounterId: string
  currentStatus: BillingStatusValue
  /** Whether a counting payment exists on the case — gates the Voided option. */
  hasPayments: boolean
  /** The case's submission route when it is externally managed (claimSubmittedAt set,
   *  PD-1739) — renders the read-only pill with the refusal text instead of the control. */
  managedRoute?: string
  /** Caption naming the status the pill carries ("Claim") — the drawer header shows it
   *  next to the payment-status pill, which can read the same word. */
  label?: string
}

/**
 * The claim-status pill as a control (port of the legacy BillingStatusControls popover):
 * clicking it opens a Select limited to the transitions the backend gate would accept
 * (see manualStatusOptions) plus the legacy "Billing date" picker, confirmed through
 * billingV2UpdateCase. When no manual transition is available (voided, or paid with no
 * payments) it renders the plain read-only pill. An externally managed case (managedRoute
 * set) also renders read-only, with the PD-1739 refusal text as the pill's tooltip.
 */
export const ClaimStatusControl = ({
  encounterId,
  currentStatus,
  hasPayments,
  managedRoute,
  label: pillLabel,
}: ClaimStatusControlProps) => {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [opened, setOpened] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)
  const [billingUpdateDate, setBillingUpdateDate] = useState<string | null>(todayBillingDate)
  const updateCase = useMutation(orpc.billingV2UpdateCase.mutationOptions())

  const options = manualStatusOptions(currentStatus, hasPayments)
  const style = BILLING_STATUS_STYLES[currentStatus]
  const label = BILLING_STATUS_LABELS[currentStatus]

  // PD-1739: the clearinghouse owns a submitted claim — no manual transitions, and the
  // tooltip carries the refusal wording (StatusPill is a plain span, hence the ref span).
  if (managedRoute) {
    return (
      <Tooltip label={managedCaseRefusal(managedRoute)} withArrow multiline w={260}>
        <span className="inline-flex w-fit">
          <StatusPill style={style} label={pillLabel}>
            {label}
          </StatusPill>
        </span>
      </Tooltip>
    )
  }

  if (options.length === 0) {
    return (
      <StatusPill style={style} label={pillLabel}>
        {label}
      </StatusPill>
    )
  }

  const handleConfirm = async () => {
    const nextStatus = selectedStatus
    if (nextStatus !== 'billed' && nextStatus !== 'denied' && nextStatus !== 'voided') return
    try {
      await updateCase.mutateAsync({
        encounterId,
        claimStatus: nextStatus,
        billingUpdateDate: normalizeBillingUpdateDate(billingUpdateDate ?? todayBillingDate()),
      })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
      void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
      setOpened(false)
      setSelectedStatus(null)
      showNotification({ color: 'green', message: 'Claim status updated' })
    } catch (error) {
      // A 400 is the transition gate refusing — its message says why.
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Failed to update claim status',
      })
    }
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      width={260}
      withArrow
    >
      <Popover.Target>
        <Button
          variant="subtle"
          onClick={() => setOpened((current) => !current)}
          styles={{
            root: {
              width: 'fit-content',
              height: 28,
              paddingInline: 12,
              borderRadius: 8,
              border: `1px solid ${style.borderColor}`,
              backgroundColor: style.backgroundColor,
              color: style.color,
              fontSize: 13,
              fontWeight: 600,
            },
            label: {
              color: style.color,
              whiteSpace: 'nowrap',
            },
          }}
        >
          {pillLabel ? <PillLabel label={pillLabel} borderColor={style.borderColor} /> : null}
          {label}
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="sm">
          <Select
            label="Claim status"
            placeholder="Select status"
            data={options.map((value) => ({ value, label: BILLING_STATUS_LABELS[value] }))}
            value={selectedStatus}
            onChange={setSelectedStatus}
            comboboxProps={{ withinPortal: false }}
            renderOption={({ option }) => {
              const optionStatus = option.value as BillingStatusValue
              const optionStyle = BILLING_STATUS_STYLES[optionStatus]
              return (
                <div
                  className="inline-flex items-center rounded-md border px-2 py-1 text-sm font-semibold"
                  style={{
                    borderColor: optionStyle.borderColor,
                    backgroundColor: optionStyle.backgroundColor,
                    color: optionStyle.color,
                  }}
                >
                  {BILLING_STATUS_LABELS[optionStatus]}
                </div>
              )
            }}
            styles={{
              input: {
                color: isBillingStatusValue(selectedStatus)
                  ? BILLING_STATUS_STYLES[selectedStatus].color
                  : BILLING_STATUS_STYLES.pending.color,
                fontWeight: 600,
              },
            }}
          />

          <DatePickerInput
            label="Billing date"
            placeholder="MM/DD/YYYY"
            value={billingUpdateDate}
            onChange={(value) => setBillingUpdateDate(value ?? todayBillingDate())}
            valueFormat="MM/DD/YYYY"
            clearable={false}
            popoverProps={{ withinPortal: false }}
            rightSection={<IconCalendar size={16} className="text-slate-500" />}
            rightSectionPointerEvents="none"
            classNames={{
              label: 'text-sm font-medium text-slate-900',
              input:
                'h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 transition outline-none focus:border-blue-500',
            }}
          />

          <Button
            onClick={() => void handleConfirm()}
            loading={updateCase.isPending}
            disabled={selectedStatus === null}
          >
            Confirm
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
